import React from 'react';
import { WasteIndex } from '../../types/dashboard';
import { CurrencyCode } from '../../utils/currency';

/** Narrow an arbitrary currency string to a known CurrencyCode, defaulting to USD */
function toCurrencyCode(currency: string): CurrencyCode {
  return (['USD', 'INR', 'EUR', 'GBP'] as const).includes(currency as CurrencyCode)
    ? (currency as CurrencyCode)
    : 'USD';
}

/**
 * WasteBreakdown component for displaying waste index and breakdown
 * AC 1: Display waste index
 * Shows empty state when optimization API data is unavailable
 * WCAG 2.1 AA compliant with accessible progress bars
 */

interface WasteBreakdownProps {
  wasteIndex: WasteIndex;
  /** Currency to display amounts in (native currency for single-provider views, USD for All Providers) */
  currency?: string;
}

export const WasteBreakdown: React.FC<WasteBreakdownProps> = ({ wasteIndex, currency = 'USD' }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: toCurrencyCode(currency),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Determine waste score color
  const getScoreColor = (score: number) => {
    if (score <= 25) return 'text-emerald-600 dark:text-emerald-400';
    if (score <= 50) return 'text-amber-600 dark:text-amber-400';
    if (score <= 75) return 'text-orange-600 dark:text-orange-400';
    return 'text-rose-600 dark:text-rose-400';
  };

  const getScoreLabel = (score: number) => {
    if (score <= 25) return 'Excellent';
    if (score <= 50) return 'Good';
    if (score <= 75) return 'Fair';
    return 'Needs Attention';
  };

  // Empty state: optimization API data unavailable
  if (!wasteIndex.dataAvailable || wasteIndex.wasteScore === null) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Waste Index</h3>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <svg
            className="mb-4 h-12 w-12 text-muted-foreground/70"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-sm font-medium text-muted-foreground">
            Waste Index Unavailable
          </p>
          <p className="mt-1 text-xs text-muted-foreground dark:text-muted-foreground">
            Optimization data is not available. The waste index will appear once the optimization service provides recommendations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Waste Index</h3>

      {/* Waste Score */}
      <div className="mb-4 text-center">
        <div className={`text-3xl font-bold ${getScoreColor(wasteIndex.wasteScore)}`}>
          {wasteIndex.wasteScore.toFixed(1)}
        </div>
        <div className="mt-1 text-sm font-medium text-muted-foreground">
          {getScoreLabel(wasteIndex.wasteScore)}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          {wasteIndex.totalWaste !== null ? formatCurrency(wasteIndex.totalWaste) : formatCurrency(0)} total waste ({wasteIndex.wastePercentage !== null ? wasteIndex.wastePercentage.toFixed(1) : '0.0'}% of spend)
        </div>
      </div>

      {/* Waste Score Progress Bar */}
      <div className="mb-4">
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-secondary"
          role="progressbar"
          aria-valuenow={wasteIndex.wasteScore}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Waste score: ${wasteIndex.wasteScore} out of 100`}
        >
          <div
            className={`h-full transition-all ${
              wasteIndex.wasteScore <= 25
                ? 'bg-emerald-500'
                : wasteIndex.wasteScore <= 50
                ? 'bg-amber-500'
                : wasteIndex.wasteScore <= 75
                ? 'bg-orange-500'
                : 'bg-rose-500'
            }`}
            style={{ width: `${wasteIndex.wasteScore}%` }}
          />
        </div>
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>0 (Best)</span>
          <span>100 (Worst)</span>
        </div>
      </div>

      {/* Waste Breakdown */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground/80">Breakdown by Category</h4>
        {wasteIndex.wasteByCategory.length > 0 ? (
          wasteIndex.wasteByCategory.map((category, index) => (
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground/80">{category.category}</span>
                <span className="font-semibold text-foreground">
                  {formatCurrency(category.amount)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"
                  role="progressbar"
                  aria-valuenow={category.percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${category.category}: ${category.percentage.toFixed(1)}%`}
                >
                  <div
                    className="h-full bg-rose-500 transition-all dark:bg-rose-600"
                    style={{ width: `${category.percentage}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-12 text-right">
                  {category.percentage.toFixed(1)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{category.description}</p>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">No category breakdown available</p>
        )}
      </div>
    </div>
  );
};
